import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Mail, Phone, MapPin, Send, CheckCircle2 } from 'lucide-react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Textarea } from './ui/textarea';
import { Label } from './ui/label';
import { Card, CardContent } from './ui/card';
import LanguageSelector from './LanguageSelector';

const Footer = () => {
  const { t } = useTranslation();
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    message: ''
  });
  const [isSubmitted, setIsSubmitted] = useState(false);

  const handleSubmit = (e) => {
    e.preventDefault();
    console.log('Form submitted:', formData);
    setIsSubmitted(true);
    setTimeout(() => {
      setIsSubmitted(false);
      setFormData({ name: '', email: '', phone: '', message: '' });
    }, 3000);
  };

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  return (
    <footer id="contact" className="bg-gradient-to-br from-gray-900 via-gray-800 to-gray-900 text-white">
      {/* Contact Form Section */}
      <div className="container mx-auto px-4 py-20">
        <div className="grid lg:grid-cols-2 gap-12 items-start">
          {/* Left - Contact Info */}
          <div>
            <div className="inline-flex items-center gap-2 bg-cyan-500/20 px-4 py-2 rounded-full mb-6">
              <Mail className="w-4 h-4 text-cyan-400" />
              <span className="text-sm font-semibold text-cyan-300">{t('footer.contactUs')}</span>
            </div>
            
            <h2 className="text-4xl lg:text-5xl font-bold mb-6">
              {t('footer.startProject')} <span className="bg-clip-text text-transparent bg-gradient-to-r from-cyan-400 to-blue-400">{t('footer.project')}</span>
            </h2>
            
            <p className="text-xl text-gray-300 mb-8 leading-relaxed">
              {t('footer.contactDescription')}
            </p>

            <div className="space-y-6">
              <div className="flex items-start gap-4">
                <div className="w-12 h-12 bg-cyan-500/20 rounded-full flex items-center justify-center flex-shrink-0">
                  <Mail className="w-6 h-6 text-cyan-400" />
                </div>
                <div>
                  <div className="font-semibold text-gray-200 mb-1">{t('footer.email')}</div>
                  <a href="mailto:sales@one2teesusa.com" className="text-cyan-400 hover:text-cyan-300 transition-colors duration-200">
                    sales@one2teesusa.com
                  </a>
                </div>
              </div>

              <div className="flex items-start gap-4">
                <div className="w-12 h-12 bg-blue-500/20 rounded-full flex items-center justify-center flex-shrink-0">
                  <Phone className="w-6 h-6 text-blue-400" />
                </div>
                <div>
                  <div className="font-semibold text-gray-200 mb-1">{t('footer.phones')}</div>
                  <a href="tel:+15613105774" className="text-blue-400 hover:text-blue-300 transition-colors duration-200 block">
                    561-310-5774
                  </a>
                  <a href="tel:+15615687620" className="text-blue-400 hover:text-blue-300 transition-colors duration-200 block">
                    561-568-7620
                  </a>
                </div>
              </div>

              <div className="flex items-start gap-4">
                <div className="w-12 h-12 bg-cyan-500/20 rounded-full flex items-center justify-center flex-shrink-0">
                  <MapPin className="w-6 h-6 text-cyan-400" />
                </div>
                <div>
                  <div className="font-semibold text-gray-200 mb-1">{t('footer.address')}</div>
                  <p className="text-gray-400">
                    6600 Georgia Ave Ste. 8<br />
                    West Palm Beach, FL 33405
                  </p>
                </div>
              </div>
            </div>

            {/* Business Hours */}
            <div className="mt-8 p-6 bg-white/5 backdrop-blur-sm rounded-xl border border-white/10">
              <h3 className="font-semibold text-gray-200 mb-3">{t('footer.businessHours')}</h3>
              <div className="space-y-2 text-sm text-gray-400">
                <div className="flex justify-between">
                  <span>{t('footer.mondaySunday')}:</span>
                  <span className="text-gray-300">9:30 AM - 6:00 PM</span>
                </div>
              </div>
            </div>
          </div>

          {/* Right - Contact Form */}
          <Card className="bg-white/10 backdrop-blur-md border-2 border-white/20 shadow-2xl">
            <CardContent className="p-8">
              {!isSubmitted ? (
                <form onSubmit={handleSubmit} className="space-y-6">
                  <div>
                    <Label htmlFor="name" className="text-gray-200 font-semibold mb-2 block">
                      {t('footer.fullName')}
                    </Label>
                    <Input
                      id="name"
                      name="name"
                      value={formData.name}
                      onChange={handleChange}
                      required
                      className="bg-white/10 border-white/20 text-white placeholder:text-gray-400 focus:border-cyan-400 h-12"
                      placeholder={t('footer.yourName')}
                    />
                  </div>

                  <div>
                    <Label htmlFor="email" className="text-gray-200 font-semibold mb-2 block">
                      {t('footer.email')}
                    </Label>
                    <Input
                      id="email"
                      name="email"
                      type="email"
                      value={formData.email}
                      onChange={handleChange}
                      required
                      className="bg-white/10 border-white/20 text-white placeholder:text-gray-400 focus:border-cyan-400 h-12"
                      placeholder="tu@email.com"
                    />
                  </div>

                  <div>
                    <Label htmlFor="phone" className="text-gray-200 font-semibold mb-2 block">
                      {t('footer.phone')}
                    </Label>
                    <Input
                      id="phone"
                      name="phone"
                      type="tel"
                      value={formData.phone}
                      onChange={handleChange}
                      className="bg-white/10 border-white/20 text-white placeholder:text-gray-400 focus:border-cyan-400 h-12"
                      placeholder="(123) 456-7890"
                    />
                  </div>

                  <div>
                    <Label htmlFor="message" className="text-gray-200 font-semibold mb-2 block">
                      {t('footer.message')}
                    </Label>
                    <Textarea
                      id="message"
                      name="message"
                      value={formData.message}
                      onChange={handleChange}
                      required
                      rows={4}
                      className="bg-white/10 border-white/20 text-white placeholder:text-gray-400 focus:border-cyan-400 resize-none"
                      placeholder={t('footer.tellUsAbout')}
                    />
                  </div>

                  <Button
                    type="submit"
                    size="lg"
                    className="w-full bg-gradient-to-r from-cyan-500 to-blue-500 hover:from-cyan-600 hover:to-blue-600 text-white font-semibold h-14 text-lg shadow-lg hover:shadow-xl transform hover:scale-105 transition-all duration-300"
                  >
                    <Send className="w-5 h-5 mr-2" />
                    {t('footer.sendMessage')}
                  </Button>
                </form>
              ) : (
                <div className="text-center py-12">
                  <div className="w-20 h-20 bg-green-500/20 rounded-full flex items-center justify-center mx-auto mb-6">
                    <CheckCircle2 className="w-10 h-10 text-green-400" />
                  </div>
                  <h3 className="text-2xl font-bold text-white mb-2">{t('footer.messageSent')}</h3>
                  <p className="text-gray-300">
                    {t('footer.thankYou')}
                  </p>
                </div>
              )}
            </CardContent>
          </Card>
        </div>
      </div>

      {/* Bottom Footer */}
      <div className="border-t border-white/10">
        <div className="container mx-auto px-4 py-8">
          <div className="flex flex-col md:flex-row justify-between items-center gap-4">
            <div className="text-center md:text-left">
              <div className="text-2xl font-bold bg-clip-text text-transparent bg-gradient-to-r from-cyan-400 to-blue-400 mb-2">
                One2Tees USA
              </div>
              <p className="text-gray-400 text-sm">
                © {new Date().getFullYear()} One2Tees USA. {t('footer.allRights')}
              </p>
            </div>
            
            <div className="flex items-center gap-6">
              <LanguageSelector variant="footer" />
              <div className="flex gap-6 text-sm text-gray-400">
                <a href="/track-order" className="hover:text-cyan-400 transition-colors duration-200">
                  {t('footer.trackOrder') || 'Track Order'}
                </a>
                <a href="/terms" className="hover:text-cyan-400 transition-colors duration-200">
                  {t('footer.termsConditions') || 'Terms & Conditions'}
                </a>
                <a href="/privacy" className="hover:text-cyan-400 transition-colors duration-200">
                  {t('footer.privacyPolicy')}
                </a>
                <a href="/shipping" className="hover:text-cyan-400 transition-colors duration-200">
                  {t('footer.shipping')}
                </a>
              </div>
            </div>
          </div>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
